import {Pagination, PaginationParams} from '@src/models/pagination';
import {api} from './client';
import {Discussion, Thread} from '@src/models/discussion';
import {Comment} from '@src/models/comment';
import {ApiUtils} from '@src/utils/ApiUtils';

export namespace CommunityApi {
  interface GetDiscussionsParams extends PaginationParams {
    search?: string;
    interestsIds?: string[];
    languages?: string[];
    status?: string;
    companyId?: string;
  }

  export const getDiscussions = async (
    params: GetDiscussionsParams
  ): Promise<Pagination<Discussion>> => {
    const {data} = await api.get<Pagination<Discussion>>('discussions', {
      params: {
        ...params,
        search: params.search || undefined,
      },
    });
    return data;
  };

  export interface AddDiscussionReq {
    title: string;
    content: string;
    interestId: string;
    language: string;
    header?: string;
    companyId?: string;
    accessType?: string;
    isPublicForAll?: boolean;
  }

  export const addDiscussion = async (
    body: AddDiscussionReq
  ): Promise<Discussion> => {
    const {data} = await api.post<Discussion>('discussions', body);
    return data;
  };

  export interface ModifyDiscussionReq {
    title: string;
    content: string;
    header?: string;
    interestId: string;
    language: string;
    companyId?: string;
    accessType?: string;
    isPublicForAll?: boolean;
  }

  export const modifyDiscussion = async (
    discussionId: string,
    body: ModifyDiscussionReq
  ): Promise<Discussion> => {
    const {data} = await api.put<Discussion>(
      `discussions/edit/${discussionId}`,
      {
        title: body.title || undefined,
        content: body.content || undefined,
        header: body.header || undefined,
        interestId: body.interestId || undefined,
        language: body.language || undefined,
        companyId: body?.companyId || undefined,
        accessType: body?.accessType || undefined,
        isPublicForAll: body?.isPublicForAll,
      }
    );
    return data;
  };

  export const getDiscussion = async (
    discussionId: string
  ): Promise<Discussion> => {
    const {data} = await api.get<Discussion>(`discussions/${discussionId}`);
    return data;
  };

  export const deleteDiscussion = async (discussionId: string) => {
    await api.delete(`discussions/remove/${discussionId}`);
  };

  interface GetThreadsParams {
    page: number;
  }

  export const getThreads = async (
    discussionId: string,
    params: GetThreadsParams
  ): Promise<Pagination<Thread>> => {
    const {data} = await api.get<Pagination<Thread>>(
      `threads/all/${discussionId}`,
      {params}
    );
    return data;
  };

  export const getComments = async (threadId: string): Promise<Comment[]> => {
    const {data} = await api.get<Comment[]>(`comments/all/${threadId}`);
    return data;
  };

  interface ChangeUserDiscussionStatusReq {
    status?: 'HIDDEN' | 'SAVED' | 'ACTIVE' | 'NOT_ACTIVE';
    subscribed?: boolean;
  }

  export const changeUserDiscussionStatus = async (
    discussionId: string,
    body: ChangeUserDiscussionStatusReq
  ) => {
    await api.patch(`discussions/status/${discussionId}`, body);
  };

  interface AddThreadReq {
    discussionId: string;
    content: string;
  }

  export const addThread = async (body: AddThreadReq): Promise<Thread> => {
    const encodedBody = ApiUtils.encodeDataToWWWFormUrlEncoded(body);
    const {data} = await api.post<Thread>('threads', encodedBody, {
      headers: ApiUtils.wwwFormUrlEncodedHeaders,
    });
    return data;
  };

  interface ModifyThreadReq {
    content: string;
  }

  export const modifyThread = async (
    threadId: string,
    body: ModifyThreadReq
  ): Promise<Thread> => {
    const encodedBody = ApiUtils.encodeDataToWWWFormUrlEncoded(body);
    const {data} = await api.put<Thread>(
      `threads/edit/${threadId}`,
      encodedBody,
      {
        headers: ApiUtils.wwwFormUrlEncodedHeaders,
      }
    );
    return data;
  };

  export const deleteThread = async (threadId: string) => {
    await api.delete(`threads/remove/${threadId}`);
  };

  interface AddCommentReq {
    content: string;
    threadId: string;
  }

  export const addComment = async (body: AddCommentReq) => {
    await api.post<Comment>('comments/add', body);
  };

  interface ModifyCommentReq {
    content: string;
  }

  export const modifyComment = async (
    commentId: string,
    body: ModifyCommentReq
  ) => {
    const encodedBody = ApiUtils.encodeDataToWWWFormUrlEncoded(body);
    const {data} = await api.put<Comment>(
      `comments/edit/${commentId}`,
      encodedBody,
      {
        headers: ApiUtils.wwwFormUrlEncodedHeaders,
      }
    );
    return data;
  };

  export const deleteComment = async (commentId: string) => {
    await api.delete(`comments/delete/${commentId}`);
  };

  export interface ReportCommentReq {
    commentId: string;
    reason: string;
  }

  export const reportComment = async (body: ReportCommentReq) => {
    await api.post(`comments/${body.commentId}/report`, {
      description: body.reason,
    });
  };

  export interface ReportThreadReq {
    threadId: string;
    reason: string;
  }

  export const reportThread = async (body: ReportThreadReq) => {
    await api.post(`threads/${body.threadId}/report`, {
      description: body.reason,
    });
  };

  export interface ReportTopicReq {
    topicId: string;
    reason: string;
  }

  export const reportTopic = async (body: ReportTopicReq) => {
    await api.post(`discussions/${body.topicId}/report`, {
      description: body.reason,
    });
  };

  interface GetMyDiscussionsParams {
    search: string;
    interestsIds: string[];
    page: number;
    languages: string[];
    status?: string;
  }

  export const getMyDiscussions = async (
    params: GetMyDiscussionsParams
  ): Promise<Pagination<Discussion>> => {
    const {data} = await api.get<Pagination<Discussion>>(
      'discussions/my/by-status',
      {
        params: {
          ...params,
          search: params.search || undefined,
        },
      }
    );
    return data;
  };
}
